/**
 * The cusp gate — how "if in doubt, don't tell" is enforced mechanically.
 *
 * A nakshatra is 13°20' wide and the Moon moves 33 arcsec per minute of clock
 * time; the Ascendant moves a degree every four minutes. So the binding
 * constraint on any chart is never the ephemeris (accurate to ~1 arcmin) — it
 * is how well the person actually knows their birth time. Someone born "about
 * 7 in the morning" carries ±30 to ±180 minutes of uncertainty, which is two
 * to three orders of magnitude larger than every other error combined.
 *
 * Rather than hope, we compute each categorical result three times — at
 * t−delta, t, and t+delta — and refuse to assert anything the three runs
 * disagree about. The UI then shows both candidates and the exact clock time
 * the boundary falls, which is genuinely useful and, crucially, true.
 */

/** How precisely the user says they know the birth time. */
export type TimePrecision = 'exact' | 'five-min' | 'half-hour' | 'few-hours' | 'unknown';

/**
 * Uncertainty half-width in minutes for each precision level.
 *
 * 'exact' is 1 minute rather than 0: a certificate states whole minutes, so the
 * true instant lies within ±30s of it, and 1 minute leaves headroom for the
 * ephemeris and ayanamsa error on top.
 */
export const PRECISION_MINUTES: Record<Exclude<TimePrecision, 'unknown'>, number> = {
  exact: 1,
  'five-min': 5,
  'half-hour': 30,
  'few-hours': 180,
};

export const PRECISION_LABELS: Record<TimePrecision, string> = {
  exact: 'To the minute — from a birth certificate',
  'five-min': 'Within about 5 minutes',
  'half-hour': 'Within about 30 minutes',
  'few-hours': 'Within a few hours',
  unknown: "I don't know the birth time",
};

/** When the time is unknown we still bound the day, for the few things that survive it. */
export const UNKNOWN_TIME_MINUTES = 12 * 60;

export function precisionToMinutes(precision: TimePrecision): number {
  return precision === 'unknown' ? UNKNOWN_TIME_MINUTES : PRECISION_MINUTES[precision];
}

export type Certain<T> =
  | { certain: true; value: T }
  | { certain: false; candidates: T[]; boundaryAt: Date | null };

/**
 * Evaluates `compute` at t−delta, t and t+delta and reports whether the result
 * is stable across all three.
 *
 * When it is not, the caller must NOT pick one. `candidates` holds the distinct
 * values in time order, and `boundaryAt` is the instant the change occurs,
 * found by bisection so the UI can tell the user exactly what time to pin down.
 *
 * @param compute  must be a *categorical* projection (sign, nakshatra, pada,
 *                 house, kuta score) — not a continuous quantity, which will
 *                 always differ slightly and always report uncertain.
 * @param key      equality for the category; defaults to strict equality.
 */
export function confident<T>(
  compute: (t: Date) => T,
  centre: Date,
  deltaMinutes: number,
  key: (value: T) => string | number = (v) => v as unknown as string,
): Certain<T> {
  const ms = deltaMinutes * 60_000;
  const early = new Date(centre.getTime() - ms);
  const late = new Date(centre.getTime() + ms);

  const vEarly = compute(early);
  const vMid = compute(centre);
  const vLate = compute(late);

  const kEarly = key(vEarly);
  const kMid = key(vMid);
  const kLate = key(vLate);

  if (kEarly === kMid && kMid === kLate) {
    return { certain: true, value: vMid };
  }

  // Distinct values, in time order.
  const candidates: T[] = [];
  const seen = new Set<string | number>();
  for (const [k, v] of [
    [kEarly, vEarly],
    [kMid, vMid],
    [kLate, vLate],
  ] as [string | number, T][]) {
    if (!seen.has(k)) {
      seen.add(k);
      candidates.push(v);
    }
  }

  // Locate the first transition by bisection, so we can name the exact time.
  const changeStart = kEarly !== kMid ? early : centre;
  const changeEnd = kEarly !== kMid ? centre : late;
  const boundaryAt = bisectBoundary(compute, changeStart, changeEnd, key);

  return { certain: false, candidates, boundaryAt };
}

/** Narrows a category change to the second, by bisection. */
function bisectBoundary<T>(
  compute: (t: Date) => T,
  before: Date,
  after: Date,
  key: (value: T) => string | number,
): Date | null {
  const kBefore = key(compute(before));
  if (kBefore === key(compute(after))) return null;

  let lo = before.getTime();
  let hi = after.getTime();
  // ~40 iterations resolves any interval to well under a second.
  for (let i = 0; i < 40 && hi - lo > 500; i++) {
    const mid = Math.floor((lo + hi) / 2);
    if (key(compute(new Date(mid))) === kBefore) lo = mid;
    else hi = mid;
  }
  return new Date(hi);
}

/**
 * Convenience wrapper for the common case: a categorical projection of a
 * single longitude, where the longitude itself is a function of time.
 */
export function confidentLongitudeCategory(
  longitudeAt: (t: Date) => number,
  categoryOf: (longitude: number) => number,
  centre: Date,
  deltaMinutes: number,
): Certain<number> {
  return confident((t) => categoryOf(longitudeAt(t)), centre, deltaMinutes, (v) => v);
}
